/**
 * Legal identity details for The Polished Version, shown on the Privacy Policy,
 * Terms and Conditions and Cookie Policy.
 *
 * Any value left as `null` is simply not shown — the pages never display
 * placeholder text. Fill these in once they are known (they are needed for a
 * complete UK privacy notice and for the Companies Act website disclosures if
 * the business trades as a limited company):
 *
 * - legalName:          the registered company name or the sole trader's name
 *                       (e.g. "Example Ltd" or "Jane Smith trading as The Polished Version").
 * - companyNumber:      Companies House number, if a limited company.
 * - registeredAddress:  registered office / postal address for legal notices.
 * - icoRegistrationNumber: ICO data protection fee registration number.
 */
export interface LegalEntity {
  tradingName: string;
  legalName: string | null;
  companyNumber: string | null;
  registeredAddress: string | null;
  icoRegistrationNumber: string | null;
  contactEmail: string;
  websiteUrl: string;
  lastUpdated: string;
}

export const LEGAL_ENTITY: LegalEntity = {
  tradingName: "The Polished Version",
  legalName: null,
  companyNumber: null,
  registeredAddress: null,
  icoRegistrationNumber: null,
  contactEmail: "hello@thepolishedversion.com",
  websiteUrl: "https://thepolishedversion.com",
  lastUpdated: "2 November 2026",
};

/** Lines describing who "we" are, built only from the details that are set. */
export function legalIdentityLines(entity: LegalEntity = LEGAL_ENTITY): string[] {
  const lines: string[] = [];
  if (entity.legalName && entity.legalName !== entity.tradingName) {
    lines.push(`${entity.tradingName} is a trading name of ${entity.legalName}.`);
  }
  if (entity.companyNumber) {
    lines.push(`Company number: ${entity.companyNumber}.`);
  }
  if (entity.registeredAddress) {
    lines.push(`Address: ${entity.registeredAddress}.`);
  }
  if (entity.icoRegistrationNumber) {
    lines.push(`ICO registration number: ${entity.icoRegistrationNumber}.`);
  }
  return lines;
}
